import { Component, OnInit } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { ApplicationDataService } from '../../../services/application.service';
import { Application } from '../../models/application';
import { UserLoginService } from '../../../services/user-login.service';
import { Property } from '../../models/property';
import { EventEmitterService } from 'services/event-emitter.service';
import { SplashService } from 'services/splash.service'; 
import { environment } from '../../../environments/environment';
import { TrackingParameters } from 'app/models/tracking-parameter';
import { ApplicationInterface } from 'app/interface/application';
import { AnalyticsService } from 'services/analytics.service';

@Component({
  selector: 'app-landing',
  templateUrl: './landing.component.html',
  styleUrls: ['./landing.component.css']
})
export class LandingComponent implements OnInit {
  errorMessage: string;
  public appFee: string = "";
  public paymentOptions: string = "";
  modelProperty: Property;
  splashHTML: string;
  public termsOfAgreementUrl: string = environment.termsOfAgreementUrl;
  trackingParams: TrackingParameters;
  rentMultiplier: string = "3X";

  constructor(
    private applicationService: ApplicationDataService,
    private router: Router,
    private eventEmitterService: EventEmitterService,
    private splashService: SplashService,
    private readonly analyticsService: AnalyticsService) {
      this.analyticsService.track(
        'HRA Application',
        'Landing',
        'Start',
        'Start',
        '',
        this.additionalAnalyticsDetails());
    this.eventEmitterService.splashChangeEmitted$.subscribe(res => this.updateSplashHTML(res));
    this.splashService.updateSplash("apply");

    const yardiKey = localStorage.getItem('YardiKey');
    const mfUnitCode = localStorage.getItem('mfunitcode');

    if(yardiKey != null && mfUnitCode != null){
      // MF unit 
      this.applicationService.getMFProperty(yardiKey, mfUnitCode).subscribe(res => this.setProperty(res.results));
    } else {
      // SF home 
      this.applicationService.getProperty(yardiKey).subscribe(res => this.setProperty(res.results));
    }
  }

  private additionalAnalyticsDetails(): any {
    try {
      return {
        'AppFee': this.appFee,
        'PaymentOptions': this.paymentOptions,
        'TrackingParams': this.trackingParams ?? {},
        'ModelProperty': this.modelProperty ?? {}
      };
    } catch (error) {
      console.error(error);
      return {};
    }
  }

  ngOnInit() {
    localStorage.removeItem('hasRentableItems');
    localStorage.removeItem('RentalApplicationKey');
    localStorage.removeItem('RentalApplicantKey');
    localStorage.removeItem('PrimaryApplicant');
    const yardiKey = localStorage.getItem('YardiKey');

    let smartHomeTrackingToken = localStorage.getItem('SmartHomeToken');
    let anyoneHomeTrackingToken = localStorage.getItem('AnyoneHomeToken');

    let uuidRegEx = /^[0-9A-F]{8}-[0-9A-F]{4}-[4][0-9A-F]{3}-[89AB][0-9A-F]{3}-[0-9A-F]{12}$/i;

    this.applicationService.getAppFeeByProperty(yardiKey).subscribe(res => this.setAppFeeAmount(res), err => this.handleAppFeeError());

    this.applicationService.getLandingPaymentOptions().subscribe(opts => this.setLandingPaymentOptions(opts));

    if(smartHomeTrackingToken) {
      if(smartHomeTrackingToken.match(uuidRegEx)) {
        this.markSmartHomeTokenAsUsed(smartHomeTrackingToken);
      }
      else {
        console.log("invalid stt");
      }
    }

    if (anyoneHomeTrackingToken) {
      if(anyoneHomeTrackingToken.match(uuidRegEx)) {
        this.markAnyoneHomeTokenAsUsed(anyoneHomeTrackingToken);
      }
      else {
        console.log("invalid aat");
      }
    }

    const property = JSON.parse( localStorage.getItem('SelectedProperty'));
    if (property != null) {
      this.setProperty(property);
    }
  }

  setProperty(property: Property[]){
    this.modelProperty = property[0];
    if (this.modelProperty.state !== null && this.modelProperty.state.toUpperCase() === "CO") {
      this.rentMultiplier = "2X";
    }

    if((property[0].min_rent != null && property[0].max_rent != null) && (property[0].min_rent == property[0].max_rent)){
      this.modelProperty.rent = property[0].min_rent.toLocaleString();
    }
    else if (property[0].min_rent != null && property[0].max_rent != null) {
      this.modelProperty.rent = property[0].min_rent.toLocaleString() + '-$'+ property[0].max_rent.toLocaleString()
    }
  }
  
  updateSplashHTML(res: string)
  {
    this.splashHTML = res;
  }

  setAppFeeAmount(res) {
    if(res && res > 0) {
      this.appFee = `Each applicant pays an application fee of $${res}`;
    }
    else{
      this.handleAppFeeError();
    }
  }

  setLandingPaymentOptions(opts) {
    this.paymentOptions = opts;
  }

  handleAppFeeError(){
    this.appFee = "Each applicant is required to pay an application fee";
  }

  onNewApplicantClick() {
    const yardiKey = localStorage.getItem('PropertyYardiKey');
    if (!yardiKey) {
      this.analyticsService.track(
        'HRA Application',
        'Landing',
        'Stop',
        'New applicant click',
        '/application/dashboard',
        this.additionalAnalyticsDetails());
      this.router.navigate(['/application/dashboard']);
    } else {

      const application = new Application();

      let referralId = localStorage.getItem('ReferralId');
      if (referralId){
        application.BrokerReferralId = referralId;
      }
      let trackingParams = localStorage.getItem('TrackingParameters');

      if (trackingParams)
      {
        this.trackingParams = JSON.parse(trackingParams);
  
        application.Source = this.trackingParams.Source;
        application.Campaign = this.trackingParams.Campaign;
        application.AdGroup = this.trackingParams.AdGroup;
        application.Medium = this.trackingParams.Medium;
        application.Keyword = this.trackingParams.Keyword;
        application.AdCopy = this.trackingParams.AdCopy;
        application.Device = this.trackingParams.Device;
      }

      try {
        this.applicationService.createApplication(application)
        .subscribe(res => this.onCreateApplicationSuccess(res),
        res => this.applicationService.handleError(res));

        localStorage.removeItem('PropertyYardiKey');
      }
      catch(e) {
        this.analyticsService.track(
          'HRA Application',
          'Landing',
          'Stop',
          'New applicant click',
          '/application/dashboard',
          this.additionalAnalyticsDetails());
        this.router.navigate(['/application/dashboard']);
      }

    }
  }

  markSmartHomeTokenAsUsed(token: string) {
    this.applicationService.markSmartHomeTokenAsUsed(token).subscribe(() => this.onSmartHomeTokenMarked());
  }

  markAnyoneHomeTokenAsUsed(token: string) {
    this.applicationService.markAnyoneHomeTokenAsUsed(token).subscribe(() => this.onAnyoneHomeTokenMarked());
  }

  onSmartHomeTokenMarked() {
    localStorage.removeItem('SmartHomeToken');
  }

  onAnyoneHomeTokenMarked() {
    localStorage.removeItem('AnyoneHomeToken');
  }
 
  goToDashboard() {
    localStorage.removeItem('PropertyYardiKey');
    this.analyticsService.track(
      'HRA Application',
      'Landing',
      'Stop',
      'Sending to dashboard',
      '/application/dashboard',
      this.additionalAnalyticsDetails());
    this.router.navigate(['/application/dashboard']);
  }

  onCreateApplicationSuccess(app: Application) {
    localStorage.setItem('RentalApplicationKey', app.RentalApplicationKey.toString());
    localStorage.setItem('RentalApplicantKey', app.PrimaryRentalApplicantKey.toString());
    this.analyticsService.track(
      'HRA Application',
      'Landing',
      'Stop',
      'New application success, sending to basic',
      '/application/basic',
      this.additionalAnalyticsDetails());
    this.router.navigate(['/application/basic']);
  }
}
